/**
 * Horizontal tab strip with dedicated side scroll buttons (no overlap with tab labels).
 */

import React, { useRef, useState, useEffect, useCallback, ReactNode } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

export interface ScrollableTabStripProps {
  children: ReactNode;
  className?: string;
  /** Re-measure scroll indicators when tab list changes */
  watchKey?: string;
}

const SCROLL_BTN =
  'flex shrink-0 items-center justify-center w-9 min-h-[44px] rounded-md border border-gray-200 bg-white text-gray-600 shadow-sm transition-colors hover:bg-gray-50 hover:text-blue-600 hover:border-blue-300 disabled:pointer-events-none disabled:opacity-35 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700 dark:hover:text-blue-400';

export function ScrollableTabStrip({ children, className = '', watchKey }: ScrollableTabStripProps) {
  const tabScrollRef = useRef<HTMLDivElement>(null);
  const [hasOverflow, setHasOverflow] = useState(false);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateScrollIndicators = useCallback(() => {
    const el = tabScrollRef.current;
    if (!el) return;
    const overflow = el.scrollWidth > el.clientWidth + 2;
    setHasOverflow(overflow);
    setCanScrollLeft(overflow && el.scrollLeft > 2);
    setCanScrollRight(overflow && el.scrollLeft + el.clientWidth < el.scrollWidth - 2);
  }, []);

  useEffect(() => {
    const el = tabScrollRef.current;
    if (!el) return;
    updateScrollIndicators();
    el.addEventListener('scroll', updateScrollIndicators, { passive: true });
    const ro = new ResizeObserver(updateScrollIndicators);
    ro.observe(el);
    if (el.parentElement) ro.observe(el.parentElement);
    return () => {
      el.removeEventListener('scroll', updateScrollIndicators);
      ro.disconnect();
    };
  }, [updateScrollIndicators, watchKey]);

  const scrollTabs = (direction: 'left' | 'right') => {
    const el = tabScrollRef.current;
    if (!el) return;
    const amount = Math.max(160, Math.round(el.clientWidth * 0.75));
    el.scrollBy({ left: direction === 'left' ? -amount : amount, behavior: 'smooth' });
  };

  return (
    <div className={`flex items-stretch gap-1 min-w-0 ${className}`}>
      {hasOverflow ? (
        <button
          type="button"
          onClick={() => scrollTabs('left')}
          disabled={!canScrollLeft}
          className={SCROLL_BTN}
          aria-label="Scroll tabs left"
        >
          <ChevronLeft className="h-5 w-5 shrink-0" />
        </button>
      ) : null}

      <div
        ref={tabScrollRef}
        className="flex min-w-0 flex-1 gap-4 overflow-x-auto overflow-y-hidden pb-1 -mb-px [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        style={{ WebkitOverflowScrolling: 'touch', msOverflowStyle: 'none' }}
      >
        {children}
      </div>

      {hasOverflow ? (
        <button
          type="button"
          onClick={() => scrollTabs('right')}
          disabled={!canScrollRight}
          className={SCROLL_BTN}
          aria-label="Scroll tabs right"
        >
          <ChevronRight className="h-5 w-5 shrink-0" />
        </button>
      ) : null}
    </div>
  );
}
